import {
  NavLink,
  useLocation,
} from "react-router-dom";

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { FaShoppingCart } from "react-icons/fa";
import jwtDecode from "jwt-decode";

import CartModal from "../../../../shared/components/CartModal";
import Api from "../../../../shared/api/api";

import { useSettings } from "../../context/SettingsContext";

import Logo from "../../assets/Logo.png";

import "../../styles/NavBar.css";

const isEnabled = (value) =>
  String(value).toLowerCase() === "true";

export default function NavBar() {
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const { settings } = useSettings();

  const isAdminPath =
    location.pathname.startsWith("/admin");

  const [showCart, setShowCart] =
    useState(false);

  const [cartCount, setCartCount] =
    useState(0);

  const [username, setUsername] =
    useState(null);

  const [isAdminUser, setIsAdminUser] =
    useState(false);

  const [menuOpen, setMenuOpen] =
    useState(false);

  const shopEnabled = isEnabled(
    settings?.route_shop_enabled
  );

  /* -----------------------------------------
     Authentication
  ------------------------------------------ */

  useEffect(() => {
    function refreshAuthentication() {
      const token =
        Api.token ||
        localStorage.getItem("token");

      if (!token) {
        setUsername(null);
        setIsAdminUser(false);
        return;
      }

      try {
        Api.setToken(token);

        const decoded = jwtDecode(token);

        if (decoded?.exp && decoded.exp * 1000 <= Date.now()) {
          Api.clearToken();
          localStorage.removeItem("token");
          localStorage.removeItem("admin");
          setUsername(null);
          setIsAdminUser(false);
          return;
        }

        const adminStatus =
          decoded?.admin === true ||
          decoded?.admin === 1 ||
          decoded?.admin === "true";

        setUsername(
          decoded?.username || null
        );

        setIsAdminUser(adminStatus);

        localStorage.setItem(
          "admin",
          String(adminStatus)
        );
      } catch (error) {
        console.error(
          "Token decode failed:",
          error
        );

        Api.clearToken();
        localStorage.removeItem("token");
        localStorage.removeItem("admin");

        setUsername(null);
        setIsAdminUser(false);
      }
    }
    refreshAuthentication();
    window.addEventListener("auth-changed", refreshAuthentication);
    return () => window.removeEventListener("auth-changed", refreshAuthentication);
  }, []);

  /* -----------------------------------------
     Cart Count
  ------------------------------------------ */

  const refreshCartCount =
    useCallback(async () => {
      try {
        const response =
          await Api.getCartItems();

        let items = [];

        if (Array.isArray(response)) {
          items = response;
        } else if (
          Array.isArray(response?.items)
        ) {
          items = response.items;
        } else if (
          Array.isArray(response?.cart)
        ) {
          items = response.cart;
        } else if (
          Array.isArray(
            response?.cart_items
          )
        ) {
          items = response.cart_items;
        } else if (
          Array.isArray(
            response?.data?.items
          )
        ) {
          items = response.data.items;
        } else if (
          Array.isArray(
            response?.data?.cart_items
          )
        ) {
          items =
            response.data.cart_items;
        }

        const total = items.reduce(
          (sum, item) =>
            sum +
            Number(item?.quantity || 0),
          0
        );

        setCartCount(total);
      } catch (error) {
        console.error(
          "Failed to refresh cart count:",
          error
        );

        setCartCount(0);
      }
    }, []);

  useEffect(() => {
    const handleCartUpdate = () => {
      refreshCartCount();
    };

    window.addEventListener(
      "cart-updated",
      handleCartUpdate
    );

    return () => {
      window.removeEventListener(
        "cart-updated",
        handleCartUpdate
      );
    };
  }, [refreshCartCount]);

  useEffect(() => {
    refreshCartCount();
  }, [refreshCartCount]);

  useEffect(() => {
    refreshCartCount();
    setMenuOpen(false);
  }, [
    location.pathname,
    refreshCartCount,
  ]);

  useEffect(() => {
    const intervalId = window.setInterval(
      refreshCartCount,
      5000
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshCartCount]);

  /* -----------------------------------------
     Actions
  ------------------------------------------ */

  const handleLogout = () => {
    Api.clearToken();

    localStorage.removeItem("token");
    localStorage.removeItem("admin");

    setUsername(null);
    setIsAdminUser(false);
    setMenuOpen(false);

    window.location.assign("/");
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
        <nav
          className={`navbar ${
            isHomePage ? "homepage-navbar" : ""
          }`}
          aria-label="Publishing navigation"
        >
        {!isHomePage && (
          <NavLink
            to="/"
            className="logo-link"
            onClick={closeMenu}
            aria-label="Avwakin Publishing home"
          >
            <img
              src={Logo}
              alt="Kenneth L. Powell"
              className="navbar-logo"
            />
          </NavLink>
        )}

        <button
          type="button"
          className="hamburger"
          aria-label={
            menuOpen
              ? "Close navigation menu"
              : "Open navigation menu"
          }
          aria-expanded={menuOpen}
          aria-controls="publishing-navigation-links"
          onClick={() =>
            setMenuOpen((previous) => !previous)
          }
        >
          <span />
          <span />
          <span />
        </button>

        <div
          id="publishing-navigation-links"
          className={`nav-links ${
            menuOpen ? "open" : ""
          }`}
        >
          <NavLink
            to="/"
            end
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive
                ? "nav-button activeLink"
                : "nav-button"
            }
          >
            Home
          </NavLink>

          <NavLink
            to="/books"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive
                ? "nav-button activeLink"
                : "nav-button"
            }
          >
            Books
          </NavLink>

          <NavLink
            to="/blog"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive
                ? "nav-button activeLink"
                : "nav-button"
            }
          >
            Blog
          </NavLink>

          {shopEnabled && (
            <NavLink
              to="/shop"
              onClick={closeMenu}
              className={({ isActive }) =>
                isActive
                  ? "nav-button activeLink"
                  : "nav-button"
              }
            >
              Merch
            </NavLink>
          )}

          <NavLink
            to="/contact"
            onClick={closeMenu}
            className={({ isActive }) =>
              isActive
                ? "nav-button activeLink"
                : "nav-button"
            }
          >
            Subscribe/Contact
          </NavLink>

          {isAdminUser && (
            <NavLink
              to="/admin/sales"
              onClick={closeMenu}
              className={({ isActive }) =>
                isActive
                  ? "nav-button activeLink"
                  : "nav-button"
              }
            >
              Admin
            </NavLink>
          )}

          {username && (
            <button
              type="button"
              className="nav-button logout-button"
              onClick={handleLogout}
            >
              Logout
            </button>
          )}

          {!isAdminPath && (
            <button
              type="button"
              className="cart-button"
              aria-label={`Open shopping cart. ${cartCount} items`}
              onClick={() =>
                setShowCart(true)
              }
            >
              <FaShoppingCart
                size={20}
                aria-hidden="true"
              />

              {cartCount > 0 && (
                <span className="cart-count">
                  {cartCount}
                </span>
              )}
            </button>
          )}
        </div>
      </nav>

      {showCart && (
        <CartModal
          onClose={() => {
            setShowCart(false);
            refreshCartCount();
          }}
        />
      )}
    </>
  );
}
